import type { Job, Project, UserProfile } from '@factoryfixinc/ats-interfaces';
import OutreachPersistence, { type EngagementMode } from './outreach.persistence';
import MeService from '@/core/shared/me/me.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { InternalError } from '@/core/shared/errors/internal.error';
import type { MessagingSource } from '../messaging/types/messaging-source.type';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { ExistingApplicationConflictError } from './existing-application-conflict.error';
import { ActiveThreadExistsError } from './active-thread-exists.error';

export interface ResumeUploadFile {
  file: File;
  base64Data: string;
}

type OutreachProfile = Pick<UserProfile, 'id' | 'nameFirst' | 'nameLast'> & {
  source?: MessagingSource;
};

/**
 * Per-candidate fate for one outreach attempt. Lets the snackbar and the throw
 * logic read off the same source of truth instead of bucketing results twice.
 *
 * - `success`   — request returned 204
 * - `conflict`  — candidate already has an application past the early-outreach
 *                 phase; the outreach was NOT sent. Distinct copy in the toast.
 * - `activeThread` — the AI recruiter already has an active conversation with this
 *                 candidate (queued or sent outreach, screening, scheduling); the request
 *                 was blocked without side effects. Not an error.
 * - `failed`    — known business failure (job inactive, copilot disabled, etc.)
 *                 thrown as an InternalError by the persistence layer.
 * - `unexpected`— anything else (network, runtime). Carries the original error
 *                 so the throw at the bottom can send something meaningful to
 *                 Sentry; the user-facing copy is the same as `failed`.
 */
type OutreachOutcome =
  | { kind: 'success'; profile: OutreachProfile }
  | { kind: 'conflict'; profile: OutreachProfile }
  | { kind: 'activeThread'; profile: OutreachProfile }
  | { kind: 'failed'; profile: OutreachProfile }
  | { kind: 'unexpected'; profile: OutreachProfile; error: unknown };

export class OutreachService {
  private persistence = new OutreachPersistence();
  private meService = new MeService();

  /**
   * Initiates manual AI outreach for one or more candidates with bulk fault
   * tolerance — one candidate's failure does not abort the others.
   */
  async initiateManualAIOutreach(
    project: Project,
    userProfiles: OutreachProfile[],
    searchSessionId?: string,
  ): Promise<void> {
    const employerId = this.meService.employer?.id;
    if (!employerId) {
      throw new Error('Could not initiate AI outreach - no employer context');
    }

    const outcomes = await Promise.all(
      userProfiles.map((profile) => this.attemptOutreach(employerId, project.jobId, profile)),
    );

    this.notifyOutreachOutcomes(outcomes, project);

    if (searchSessionId) {
      outcomes.forEach((outcome) => {
        if (outcome.kind !== 'success') return;

        void TrackingService.trackAction(
          TrackingActionName.TALENT_SEARCH_ACTION,
          {
            search_session_id: searchSessionId,
            action_type: 'recruiter_outreach',
            pro_user_profile_id: outcome.profile.id,
            ...(project.jobId ? { job_id: project.jobId } : {}),
          },
          {
            eventId: crypto.randomUUID(),
            occurredAt: new Date().toISOString(),
          },
        );
      });
    }

    // Only escalate truly unexpected errors. Known failures (conflict,
    // job-inactive, etc.) are conveyed by the snackbar above, and the modal
    // closes on resolve — keeping it open on a partial success would leave
    // the already-succeeded candidates in the selection and risk duplicate
    // sends if the user retries. Capture the unexpected errors ourselves
    // because the modal's catch only escalates non-InternalError, but our
    // `unexpectedly` throw IS an InternalError.
    const unexpectedErrors = outcomes.flatMap((o) => (o.kind === 'unexpected' ? [o.error] : []));
    if (unexpectedErrors.length > 0) {
      unexpectedErrors.forEach((error) => ErrorService.captureException(error));
      throw new InternalError('One or more AI outreach requests failed unexpectedly', {
        data: { project: project.jobId, unexpectedErrors },
      });
    }
  }

  private async attemptOutreach(
    employerId: number,
    jobId: Job['id'],
    profile: OutreachProfile,
  ): Promise<OutreachOutcome> {
    try {
      await this.persistence.initiateManualOutreach(employerId, profile.id, jobId);
      return { kind: 'success', profile };
    } catch (error) {
      if (error instanceof ExistingApplicationConflictError) {
        return { kind: 'conflict', profile };
      }
      if (error instanceof ActiveThreadExistsError) {
        return { kind: 'activeThread', profile };
      }
      if (error instanceof InternalError) {
        return { kind: 'failed', profile };
      }
      return { kind: 'unexpected', profile, error };
    }
  }

  /**
   * Uploads candidate resumes for a job
   * Supports bulk uploads with fault tolerance via Promise.allSettled
   *
   * @param jobId - The job ID to upload resumes for
   * @param files - Array of files with their base64 data
   * @param engagementMode - How the AI recruiter should handle these candidates
   * @returns Object containing success and failure counts
   */
  async uploadCandidateResumes(
    jobId: Job['id'],
    files: ResumeUploadFile[],
    engagementMode: EngagementMode,
    trackingContext?: { source: string },
  ): Promise<{ successCount: number; failureCount: number }> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new Error('Could not upload candidate resumes - no employer context');
    }

    // Create parallel requests for fault tolerance
    const promises = files.map((fileData) => {
      return this.persistence.uploadCandidateResume(jobId, {
        resumeDataUrl: fileData.base64Data,
        engagementMode,
        fileName: fileData.file.name,
      });
    });

    // Wait for all requests to complete (don't fail fast)
    const results = await Promise.allSettled(promises);

    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failureCount = results.filter((r) => r.status === 'rejected').length;

    // Show aggregated user feedback
    this.showSnackbarForUploadResults(results, files.length);

    // Fire tracking events
    if (trackingContext) {
      const totalCount = files.length;

      if (successCount > 0) {
        TrackingService.trackAction(TrackingActionName.RESUME_UPLOAD_COMPLETED, {
          source: trackingContext.source,
          job_id: jobId,
          engagement_mode: engagementMode,
          file_count: successCount,
          total_count: totalCount,
        });
      }

      if (failureCount > 0) {
        TrackingService.trackAction(TrackingActionName.RESUME_UPLOAD_FAILED, {
          source: trackingContext.source,
          job_id: jobId,
          engagement_mode: engagementMode,
          failed_count: failureCount,
          total_count: totalCount,
        });
      }
    }

    return { successCount, failureCount };
  }

  /**
   * Shows user-friendly feedback snackbar for resume upload results
   */
  private showSnackbarForUploadResults(
    results: PromiseSettledResult<unknown>[],
    totalFiles: number,
  ): void {
    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failureCount = results.filter((r) => r.status === 'rejected').length;

    let text: string;
    let variant: 'success' | 'critical' | 'caution';

    if (failureCount === 0) {
      text =
        successCount === 1
          ? 'Resume uploaded successfully'
          : `${successCount} resumes uploaded successfully`;
      variant = 'success';
    } else if (successCount === 0) {
      text =
        failureCount === 1 ? 'Failed to upload resume' : `Failed to upload ${failureCount} resumes`;
      variant = 'critical';
    } else {
      text = `${successCount} of ${totalFiles} resumes uploaded`;
      variant = 'caution';
    }

    SnackbarService.showSnackbar({
      variant,
      text,
      appendIcon: 'close',
      horizontalPosition: 'left',
    });
  }

  /**
   * One toast per outreach call. Two paths the reader has to understand:
   *
   *   1. Everyone landed → green success line.
   *   2. Anything else   → "X added • Y already have applications • Z failed",
   *                        critical when nothing succeeded, caution otherwise.
   */
  private notifyOutreachOutcomes(outcomes: OutreachOutcome[], project: Project): void {
    const succeeded = outcomes.filter((o) => o.kind === 'success').length;
    const activeThread = outcomes.filter((o) => o.kind === 'activeThread').length;
    const conflicted = outcomes.filter((o) => o.kind === 'conflict').length;
    const failed = outcomes.length - succeeded - activeThread - conflicted;
    const isBulk = outcomes.length > 1;

    const parts: string[] = [];
    if (succeeded > 0) parts.push(addedText(succeeded, isBulk, project));
    if (activeThread > 0) parts.push(activeThreadText(activeThread, isBulk));
    if (conflicted > 0) parts.push(conflictText(conflicted, isBulk));
    if (failed > 0) parts.push(failedText(failed, isBulk));

    const hasProblem = conflicted + failed > 0;
    SnackbarService.showSnackbar({
      variant: hasProblem ? (succeeded + activeThread > 0 ? 'caution' : 'critical') : 'success',
      text: parts.join(' • '),
      appendIcon: 'close',
      horizontalPosition: 'left',
    });
  }
}

function addedText(n: number, isBulk: boolean, project: Project): string {
  const added = `${candidateLabel(n, isBulk)} added to ${project.title} (#${project.jobId})`;
  return `${added}. AI outreach queued.`;
}

function candidateLabel(n: number, isBulk: boolean): string {
  if (!isBulk) return 'Candidate';
  return n === 1 ? '1 candidate' : `${n} candidates`;
}

function activeThreadText(n: number, isBulk: boolean): string {
  if (!isBulk) {
    return (
      'Your AI recruiter is already working with this candidate. ' +
      'No additional action is needed.'
    );
  }
  return n === 1
    ? '1 candidate already being handled by your AI recruiter'
    : `${n} candidates already being handled by your AI recruiter`;
}

function conflictText(n: number, isBulk: boolean): string {
  if (!isBulk) return 'Candidate already has an application for this job';
  const subject = n === 1 ? 'candidate already has' : 'candidates already have';
  return `${n} ${subject} an application for this job`;
}

function failedText(n: number, isBulk: boolean): string {
  if (!isBulk) return 'Failed to initiate outreach';
  return n === 1 ? '1 candidate failed' : `${n} candidates failed`;
}
